import { useState, useEffect } from "react";
import SEO from "../seo";
import {
  APP_DATA,
  TRANSACTION_FEES,
  PLANS_PRICING,
  STAFF_ACCOUNTS,
  THRESHOLD_SYSTEM,
} from "../constant_data";
const PricingPlans = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "yearly-comparison",
        "starter",
        "growth",
        "pro",
        "enterprise",
        "key-details",
        "monthly-comparison",
        "all-features",
        "customer-experience",
        "threshold-system",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="xPage Pricing Plans – AI-Powered Online Store Plans"
        description="Compare xPage pricing plans from Starter to Enterprise. All plans include AI-powered tools, mobile-first designs, and powerful features to help you scale your online store."
        keywords="xPage, pricing plans, AI online store, Starter plan, Growth plan, Pro plan, Enterprise plan"
        canonical="https://help.xpage.ai/help/pricing-plans/pricing-plans"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title Group */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            xPage PricingPlans Plans
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            xPage is designed for every type of seller, from first-time brand
            builders to full-scale enterprises. All plans include AI-powered
            tools, mobile-first designs, and powerful features to help you scale
            faster.
          </p>
        </div>

        {/* At a Glance: Yearly Plan Comparison */}
        <div id="yearly-comparison" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            At a Glance: Yearly Plan Comparison
          </h2>
          <table className="w-full mb-4">
            <tbody className="border divide-y divide-systemBorder">
              <tr className="bg-gray-400/10 transition-colors">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Feature</strong>
                </td>
                <td className="p-3 align-top text-center border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Starter</strong>
                </td>
                <td className="p-3 align-top text-center border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Growth (Popular)
                  </strong>
                </td>
                <td className="p-3 align-top text-center border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Pro</strong>
                </td>
                <td className="p-3 align-top text-center">
                  <strong className="text-systemTitle text-sm">
                    Enterprise
                  </strong>
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Price (Monthly)
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.STARTER.MONTHLY} /mo
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.GROWTH.MONTHLY} /mo
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.PRO.MONTHLY} /mo
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  ${PLANS_PRICING.ENTERPRISE.MONTHLY} /mo
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Price (Yearly)
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.STARTER.YEARLY} /yr
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.GROWTH.YEARLY} /yr
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.PRO.YEARLY} /yr
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  ${PLANS_PRICING.ENTERPRISE.YEARLY} /yr
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Card Processing Rate
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.STARTER.CARD}.00%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.GROWTH.CARD}.00%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.PRO.CARD}.00%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  {TRANSACTION_FEES.ENTERPRISE.CARD}%
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    COD Processing Rate
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.STARTER.COD}%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.GROWTH.COD}%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.PRO.COD}%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  {TRANSACTION_FEES.ENTERPRISE.COD}%
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Staff Accounts
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {STAFF_ACCOUNTS.STARTER}
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {STAFF_ACCOUNTS.GROWTH}
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {STAFF_ACCOUNTS.PRO}
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  {STAFF_ACCOUNTS.ENTERPRISE}
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Best For</strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  Getting Started
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  Growing Businesses
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  Professional Sellers
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  Large Businesses
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Detailed Plan Breakdown */}
        <div className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Detailed Plan Breakdown
          </h2>

          {/* Starter Plan */}
          <div
            id="starter"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder mb-4 scroll-mt-20"
          >
            <h3 className="text-systemTitle text-lg font-semibold mb-2">
              1. Starter
            </h3>
            <p className="text-systemLabel text-sm mb-3">
              <strong>Price:</strong> ${PLANS_PRICING.STARTER.MONTHLY}/month
              (billed as ${PLANS_PRICING.STARTER.YEARLY}.00 yearly)
            </p>

            <h4 className="text-systemTitle text-sm font-semibold mb-2">
              Core Features:
            </h4>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1 mb-3">
              <li>AI-Powered Online Store</li>
              <li>Unlimited AI Landing Pages</li>
              <li>Unlimited Products & Collections</li>
              <li>High-Converting Checkout</li>
              <li>Advanced Analytics & Real-Time Reports</li>
              <li>Multiple Payment Gateways (Stripe, PayPal, COD, etc.)</li>
              <li>Order Email Updates</li>
              <li>24/7 Support</li>
              <li>Unlimited Web Hosting</li>
              <li>Custom Domain</li>
              <li>Free SSL Certificate</li>
            </ul>

            <h4 className="text-systemTitle text-sm font-semibold mb-2">
              Transaction Fees:
            </h4>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
              <li>
                Card Rate: starting at {TRANSACTION_FEES.STARTER.CARD}.00% (via
                3rd-party payment providers)
              </li>
              <li>
                Cash on Delivery rates starting at{" "}
                {TRANSACTION_FEES.STARTER.COD}%
              </li>
            </ul>

            <p className="text-systemLabel text-sm mt-3">
              <strong>Staff Accounts:</strong> {STAFF_ACCOUNTS.STARTER}
            </p>
            <p className="text-systemLabel text-sm">
              <strong>Ideal for:</strong> Individuals or new businesses just
              getting started.
            </p>
          </div>

          {/* Growth Plan */}
          <div
            id="growth"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder mb-4 scroll-mt-20"
          >
            <h3 className="text-systemTitle text-lg font-semibold mb-2">
              2. Growth (POPULAR)
            </h3>
            <p className="text-systemLabel text-sm mb-3">
              <strong>Price:</strong> ${PLANS_PRICING.GROWTH.MONTHLY}/month
              (billed as ${PLANS_PRICING.GROWTH.YEARLY}.00 yearly)
            </p>

            <p className="text-systemLabel text-sm mb-3">
              <strong>Includes everything in Starter, plus:</strong>
            </p>

            <p className="text-systemLabel text-sm mb-2">
              <strong>Staff Accounts:</strong>
              {STAFF_ACCOUNTS.GROWTH}
            </p>

            <h4 className="text-systemTitle text-sm font-semibold mb-2">
              Marketing & Growth Tools:
            </h4>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1 mb-3">
              <li>Unlimited Contacts</li>
              <li>Customer Segmentation</li>
              <li>Abandoned Checkout Recovery</li>
              <li>Discount Codes & Product Bundles</li>
              <li>Event Tracking</li>
              <li>Referral & Affiliate Program</li>
              <li>International Selling: Multi-Language Support</li>
            </ul>

            <h4 className="text-systemTitle text-sm font-semibold mb-2">
              Transaction Fees:
            </h4>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
              <li>
                Card Rate: starting at {TRANSACTION_FEES.GROWTH.CARD}.00% (via
                3rd-party payment providers) (↓ from Starter)
              </li>
              <li>
                Cash on Delivery rates starting at {TRANSACTION_FEES.GROWTH.COD}
                % (↓ from Starter)
              </li>
            </ul>

            <p className="text-systemLabel text-sm mt-3">
              <strong>Ideal for:</strong> Businesses that are growing and need
              marketing tools and staff access.
            </p>
          </div>

          {/* Pro Plan */}
          <div
            id="pro"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder mb-4 scroll-mt-20"
          >
            <h3 className="text-systemTitle text-lg font-semibold mb-2">
              3. Pro
            </h3>
            <p className="text-systemLabel text-sm mb-3">
              <strong>Price:</strong> ${PLANS_PRICING.PRO.MONTHLY}/month (billed
              as ${PLANS_PRICING.PRO.YEARLY}.00 yearly)
            </p>

            <p className="text-systemLabel text-sm mb-3">
              <strong>Includes everything in Growth, plus:</strong>
            </p>

            <p className="text-systemLabel text-sm mb-3">
              <strong>Staff Accounts:</strong> {STAFF_ACCOUNTS.PRO}
            </p>
            <p className="text-systemLabel text-sm mb-3">
              <strong>International Selling:</strong> Adds Duties & Import Taxes
              Calculation
            </p>

            <h4 className="text-systemTitle text-sm font-semibold mb-2">
              Transaction Fees:
            </h4>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
              <li>
                Card Rate starting at {TRANSACTION_FEES.PRO.CARD}.00% (via
                3rd-party payment providers) (↓ from Growth)
              </li>
              <li>
                Cash on Delivery rates starting at {TRANSACTION_FEES.PRO.COD}%
                (↓ from Growth)
              </li>
            </ul>

            <p className="text-systemLabel text-sm mt-3">
              <strong>Ideal for:</strong> Professional sellers scaling their
              operations with a larger team and international sales.
            </p>
          </div>

          {/* Enterprise Plan */}
          <div
            id="enterprise"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder mb-6 scroll-mt-20"
          >
            <h3 className="text-systemTitle text-lg font-semibold mb-2">
              4. Enterprise
            </h3>
            <p className="text-systemLabel text-sm mb-3">
              <strong>Price:</strong> ${PLANS_PRICING.ENTERPRISE.MONTHLY}/month
              (billed as ${PLANS_PRICING.ENTERPRISE.YEARLY}.00 yearly)
            </p>

            <p className="text-systemLabel text-sm mb-3">
              <strong>Includes everything in Pro, plus:</strong>
            </p>

            <p className="text-systemLabel text-sm mb-3">
              <strong>Staff Accounts:</strong> {STAFF_ACCOUNTS.ENTERPRISE}
            </p>

            <h4 className="text-systemTitle text-sm font-semibold mb-2">
              Transaction Fees:
            </h4>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
              <li>
                Card Rate starting at {TRANSACTION_FEES.ENTERPRISE.CARD}% (via
                3rd-party payment providers) (Lowest rate)
              </li>
              <li>
                Cash on Delivery rates {TRANSACTION_FEES.ENTERPRISE.COD}% (No
                additional fees)
              </li>
            </ul>

            <p className="text-systemLabel text-sm mt-3">
              <strong>Ideal for:</strong> Large businesses with high sales
              volume requiring the lowest possible transaction fees and maximum
              staff capacity.
            </p>
          </div>
        </div>

        {/* Key Details */}
        <div id="key-details" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Key Details
          </h2>
          <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
            <li>No Setup Fees</li>
            <li>No Hidden Costs</li>
            <li>No Credit Card Required to view plans or start a trial</li>
            <li>
              Pay yearly to save up to 20% compared to the monthly billing rate
            </li>
          </ul>
        </div>

        {/* Monthly Plan Comparison */}
        <div id="monthly-comparison" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            At a Glance: Monthly Plan Comparison
          </h2>
          <p className="text-systemLabel text-sm mb-3">
            Paying monthly offers flexibility with a slightly higher cost per
            month compared to the discounted yearly billing. All plans include
            core features like an AI-powered online store, unlimited products,
            and 24/7 support.
          </p>

          <table className="w-full">
            <tbody className="border divide-y divide-systemBorder">
              <tr className="bg-gray-400/10 transition-colors">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Feature</strong>
                </td>
                <td className="p-3 align-top text-center border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Starter</strong>
                </td>
                <td className="p-3 align-top text-center border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Growth (Popular)
                  </strong>
                </td>
                <td className="p-3 align-top text-center border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Pro</strong>
                </td>
                <td className="p-3 align-top text-center">
                  <strong className="text-systemTitle text-sm">
                    Enterprise
                  </strong>
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Monthly Price
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  ${PLANS_PRICING.STARTER.MONTHLY} /mo
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  $29 /mo
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  $69 /mo
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  $399 /mo
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Card Processing Rate
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.STARTER.CARD}.00%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.GROWTH.CARD}.00%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.PRO.CARD}.00%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  {TRANSACTION_FEES.ENTERPRISE.CARD}%
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    COD Processing Rate
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.STARTER.COD}%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.GROWTH.COD}%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {TRANSACTION_FEES.PRO.COD}%
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  {TRANSACTION_FEES.ENTERPRISE.COD}%
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Staff Accounts
                  </strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {STAFF_ACCOUNTS.STARTER}
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {STAFF_ACCOUNTS.GROWTH}
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  {STAFF_ACCOUNTS.PRO}
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  {STAFF_ACCOUNTS.ENTERPRISE}
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Best For</strong>
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  Getting Started
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  Growing Businesses
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm border-r border-systemBorder">
                  Professional Sellers
                </td>
                <td className="p-3 align-top text-center text-systemLabel text-sm">
                  Large Businesses
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Features on all plans */}
        <div id="all-features" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Features on all plans
          </h2>
          <table className="w-full">
            <tbody className="border divide-y divide-systemBorder">
              <tr className="bg-gray-400/10 transition-colors">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Feature</strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Description
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Core Features
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  AI-powered builder, unlimited products, mobile-first design,
                  custom domain, multi-language/currency
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Payment Checkout
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Stripe, PayPal, Klarna, COD, customizable checkout,
                  transparent fees
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Analytics Marketing
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Real-time analytics, A/B testing, abandoned cart, referral
                  tools, bundles
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Integrations
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Google Analytics and tracking events built in the system.
                  PayPal, generate product from links, image and prompt
                  AliExpress, segmentation tools +COD integration in the system
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Account Management
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Brand identity, Policies…Staff roles, order management,
                  fulfillment tracking
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Customer Experience
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Optimized LPs, testimonials, flexible checkout, localized
                  stores, customer profiles
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Threshold System
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Sales-based billing, $15 threshold start, dynamic increases
                  with growth
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Customer Experience */}
        <div id="customer-experience" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Customer Experience
          </h2>
          <table className="w-full">
            <tbody className="border divide-y divide-systemBorder">
              <tr className="bg-gray-400/10 transition-colors">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">Feature</strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Description
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Mobile-Optimized Pages
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Fast-loading, mobile-first landing pages designed to boost
                  engagement and reduce bounce rates
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Smart Conversion Blocks
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Built-in sections like testimonials, scarcity timers, and
                  product guarantees to increase trust and sales
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Localized Checkout Experience
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Region-based checkout fields and payment options tailored to
                  customer preferences
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Customer Profile System
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Centralized dashboard for customers to track orders, update
                  info, and manage preferences
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    AI-Powered Personalization
                  </strong>
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Automatically adapts content and layout based on browsing
                  behavior and purchase intent
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Threshold System */}
        <div id="threshold-system" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Threshold System
          </h2>

          <table className="w-full mb-4">
            <tbody className="border divide-y divide-systemBorder">
              <tr className="bg-gray-400/10 transition-colors">
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Threshold
                  </strong>
                </td>
                <td className="p-3 align-top border-r border-systemBorder">
                  <strong className="text-systemTitle text-sm">
                    Sales Covered
                  </strong>
                </td>
                <td className="p-3 align-top">
                  <strong className="text-systemTitle text-sm">
                    When Applied
                  </strong>
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $15
                </td>
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $1,000
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  All sellers start here
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $30
                </td>
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $5,000
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  After first successful $30 payment
                </td>
              </tr>
              <tr>
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $60
                </td>
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $10,000
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  After paying the $100 threshold
                </td>
              </tr>
              <tr className="bg-gray-400/10">
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $120+
                </td>
                <td className="p-3 align-top border-r border-systemBorder text-systemLabel text-sm">
                  $20,000+
                </td>
                <td className="p-3 align-top text-systemLabel text-sm">
                  Based on consistent payment history
                </td>
              </tr>
            </tbody>
          </table>

          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <h3 className="text-systemTitle text-sm font-semibold mb-2">Tip</h3>
            <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
              <li>
                Thresholds decrease if payments fail or your store becomes
                inactive
              </li>
              <li>If inactive for 60+ days, the threshold resets to $15</li>
              <li>
                Enterprise sellers can request custom thresholds as they scale
              </li>
              <li>
                Threshold applies to transaction fees only, not your
                subscription
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#yearly-comparison"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "yearly-comparison"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Yearly Plan Comparison
              </a>
            </li>

            <li>
              <a
                href="#starter"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "starter"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Starter Plan
              </a>
            </li>
            <li>
              <a
                href="#growth"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "growth"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Growth Plan
              </a>
            </li>
            <li>
              <a
                href="#pro"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pro"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pro Plan
              </a>
            </li>
            <li>
              <a
                href="#enterprise"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "enterprise"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Enterprise Plan
              </a>
            </li>
            <li>
              <a
                href="#key-details"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-details"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key Details
              </a>
            </li>
            <li>
              <a
                href="#monthly-comparison"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "monthly-comparison"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Monthly Comparison
              </a>
            </li>
            <li>
              <a
                href="#all-features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "all-features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                All Features
              </a>
            </li>
            <li>
              <a
                href="#customer-experience"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "customer-experience"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Customer Experience
              </a>
            </li>
            <li>
              <a
                href="#threshold-system"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "threshold-system"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Threshold System
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;
